import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { uploadImage, apiErr } from "@/lib/adminApi";
import { imgUrl } from "@/lib/site";
import { Loader2, Upload, X } from "lucide-react";

const BADGES = ["bestseller", "legendary", "limited", "premium"];
const empty = { name: "", subtitle: "", badge: "premium", price: "", original_price: "", image: "", description: "", tags: "", rating: "5.0" };

export const PackForm = ({ open, onOpenChange, initial, onSave }) => {
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setError("");
      if (initial) {
        setForm({
          name: initial.name || "", subtitle: initial.subtitle || "", badge: initial.badge || "premium",
          price: String(initial.price ?? ""), original_price: initial.original_price != null ? String(initial.original_price) : "",
          image: initial.image || "", description: initial.description || "",
          tags: (initial.tags || []).join(", "), rating: String(initial.rating ?? "5.0"),
        });
      } else setForm(empty);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const res = await uploadImage(file);
      set("image", res.data.url);
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price) {
      setError("Name und Preis sind erforderlich.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      name: form.name.trim(),
      subtitle: form.subtitle.trim(),
      badge: form.badge,
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      image: form.image,
      description: form.description.trim(),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      rating: parseFloat(form.rating) || 5.0,
    };
    try {
      await onSave(payload, initial?.id);
      onOpenChange(false);
    } catch (err) {
      setError(apiErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pack-form" className="max-w-lg border-white/10 bg-[#0f0f11] text-white max-h-[90vh] overflow-y-auto">
        <DialogTitle className="font-display font-bold text-xl">{initial ? "Pack bearbeiten" : "Neues Pack"}</DialogTitle>
        <DialogDescription className="sr-only">Formular zum Verwalten eines Packs</DialogDescription>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Field label="Name *"><input data-testid="form-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
          <Field label="Untertitel"><input data-testid="form-subtitle" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className={inp} /></Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Preis (€) *"><input data-testid="form-price" type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className={inp} /></Field>
            <Field label="Alter Preis (€)"><input data-testid="form-original" type="number" step="0.01" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} className={inp} /></Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Badge">
              <select data-testid="form-badge" value={form.badge} onChange={(e) => set("badge", e.target.value)} className={inp}>
                {BADGES.map((bd) => <option key={bd} value={bd}>{bd}</option>)}
              </select>
            </Field>
            <Field label="Bewertung"><input data-testid="form-rating" type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => set("rating", e.target.value)} className={inp} /></Field>
          </div>

          <Field label="Tags (mit Komma trennen)"><input data-testid="form-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="OG Skin, Full Access, Sofort-Lieferung" className={inp} /></Field>
          <Field label="Beschreibung"><textarea data-testid="form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inp} /></Field>

          <Field label="Bild">
            <div className="flex items-center gap-3">
              {form.image ? (
                <div className="relative">
                  <img src={imgUrl(form.image)} alt="preview" className="w-16 h-20 object-cover object-top rounded border border-white/10" />
                  <button type="button" onClick={() => set("image", "")} className="absolute -top-2 -right-2 bg-[#ff0055] rounded-full p-0.5"><X size={12} /></button>
                </div>
              ) : (
                <div className="w-16 h-20 rounded border border-dashed border-white/20 grid place-items-center text-[#54545c] text-xs">kein Bild</div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="form-file" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 border border-white/15 rounded-full px-4 py-2.5 text-sm hover:border-white/40 transition-colors">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploading ? "Lädt…" : "Bild hochladen"}
              </button>
            </div>
          </Field>

          {error && <div data-testid="form-error" className="text-sm text-[#ff0055] font-mono">{error}</div>}

          <button data-testid="form-submit" type="submit" disabled={saving || uploading} className="w-full bg-[#e2ff00] text-black font-mono text-sm uppercase tracking-[0.2em] py-3.5 rounded-full hover:bg-white transition-colors disabled:opacity-60">
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const inp = "w-full bg-black/40 border border-white/10 focus:border-[#e2ff00] rounded-md px-3 py-2.5 outline-none text-sm";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#8b8b93] mb-1.5">{label}</span>
    {children}
  </label>
);
